/**
 * Calculated fields (ADR-0008, ADR-0028). A formula is a small, safe
 * arithmetic expression over other numeric field keys: numbers, keys,
 * `+ - * /`, parentheses and unary minus. Nothing is ever `eval`ed.
 *
 * The result is stored in the answers like any other value, so rules,
 * validation, reporting and the PDF read it without re-evaluating. The
 * device computes it on every change and the server recomputes it on sync,
 * so a tampered or stale value never survives (see `computeCalculated`).
 */
import type { Answers, FieldItem, Template } from './schema.js';

type Node =
  | { k: 'num'; v: number }
  | { k: 'ref'; key: string }
  | { k: 'neg'; e: Node }
  | { k: 'bin'; op: '+' | '-' | '*' | '/'; a: Node; b: Node };

type Token = { t: 'num'; v: number } | { t: 'id'; v: string } | { t: 'op'; v: '+' | '-' | '*' | '/' } | { t: '(' } | { t: ')' };

export class FormulaError extends Error {}

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  const re = /\s*(?:(\d+(?:\.\d+)?|\.\d+)|([a-zA-Z_][\w.]*)|([+\-*/])|(\()|(\)))/y;
  let i = 0;
  while (i < src.length) {
    if (/\s/.test(src[i]!)) {
      i++;
      continue;
    }
    re.lastIndex = i;
    const m = re.exec(src);
    if (!m) throw new FormulaError(`Unexpected "${src[i]}" in formula`);
    if (m[1]) out.push({ t: 'num', v: Number(m[1]) });
    else if (m[2]) out.push({ t: 'id', v: m[2] });
    else if (m[3]) out.push({ t: 'op', v: m[3] as '+' });
    else if (m[4]) out.push({ t: '(' });
    else out.push({ t: ')' });
    i = re.lastIndex;
  }
  return out;
}

/** Parse a formula into a tree. Throws FormulaError when it is malformed. */
export function parseFormula(src: string): Node {
  const tokens = tokenize(src);
  let pos = 0;
  const peek = () => tokens[pos];
  const isOp = (v: string) => {
    const tk = peek();
    return tk?.t === 'op' && tk.v === v;
  };
  const expr = (): Node => {
    let a = term();
    while (isOp('+') || isOp('-')) {
      const op = (tokens[pos++] as { v: '+' | '-' }).v;
      a = { k: 'bin', op, a, b: term() };
    }
    return a;
  };
  const term = (): Node => {
    let a = unary();
    while (isOp('*') || isOp('/')) {
      const op = (tokens[pos++] as { v: '*' | '/' }).v;
      a = { k: 'bin', op, a, b: unary() };
    }
    return a;
  };
  const unary = (): Node => {
    if (isOp('-')) {
      pos++;
      return { k: 'neg', e: unary() };
    }
    if (isOp('+')) {
      pos++;
      return unary();
    }
    return primary();
  };
  const primary = (): Node => {
    const tk = tokens[pos++];
    if (!tk) throw new FormulaError('Formula ends too early');
    if (tk.t === 'num') return { k: 'num', v: tk.v };
    if (tk.t === 'id') return { k: 'ref', key: tk.v };
    if (tk.t === '(') {
      const e = expr();
      if (tokens[pos++]?.t !== ')') throw new FormulaError('Unbalanced parentheses');
      return e;
    }
    throw new FormulaError('Malformed formula');
  };
  if (tokens.length === 0) throw new FormulaError('Empty formula');
  const tree = expr();
  if (pos !== tokens.length) throw new FormulaError('Malformed formula');
  return tree;
}

/** Field keys a formula reads. Returns [] for a malformed formula. */
export function formulaRefs(formula: string | undefined): string[] {
  if (!formula) return [];
  let tree: Node;
  try {
    tree = parseFormula(formula);
  } catch {
    return [];
  }
  const out = new Set<string>();
  const walk = (n: Node): void => {
    if (n.k === 'ref') out.add(n.key);
    else if (n.k === 'neg') walk(n.e);
    else if (n.k === 'bin') {
      walk(n.a);
      walk(n.b);
    }
  };
  walk(tree);
  return [...out];
}

function toNumber(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Evaluate a formula against field values. Returns null when an input is
 * missing or not a number, on division by zero, or when the formula is
 * malformed. Results are rounded to 6 decimals.
 */
export function evaluateFormula(formula: string, values: Record<string, unknown>): number | null {
  let tree: Node;
  try {
    tree = parseFormula(formula);
  } catch {
    return null;
  }
  const ev = (n: Node): number | null => {
    switch (n.k) {
      case 'num':
        return n.v;
      case 'ref':
        return toNumber(values[n.key]);
      case 'neg': {
        const v = ev(n.e);
        return v === null ? null : -v;
      }
      case 'bin': {
        const a = ev(n.a);
        const b = ev(n.b);
        if (a === null || b === null) return null;
        if (n.op === '+') return a + b;
        if (n.op === '-') return a - b;
        if (n.op === '*') return a * b;
        return b === 0 ? null : a / b;
      }
    }
  };
  const r = ev(tree);
  return r === null || !Number.isFinite(r) ? null : Math.round(r * 1e6) / 1e6;
}

function computeInto(fields: FieldItem[], target: Record<string, unknown>, scope: () => Record<string, unknown>): boolean {
  let changed = false;
  for (const f of fields) {
    if (f.type !== 'calculated') continue;
    const r = evaluateFormula(f.formula ?? '', scope());
    if (r === null) {
      if (f.key in target) {
        delete target[f.key];
        changed = true;
      }
    } else if (target[f.key] !== r) {
      target[f.key] = r;
      changed = true;
    }
  }
  return changed;
}

/**
 * Answers with every calculated field (de)filled from its formula: top-level
 * fields read the other top-level fields; fields inside a repeating group
 * read their row first, then the top level. Calculated fields may build on
 * each other in any order. Pure: the input is not changed.
 */
export function computeCalculated(template: Template, answers: Answers): Answers {
  const top = template.sections.flatMap((s) => s.items.filter((i): i is FieldItem => i.kind === 'field'));
  const calculated = top.filter((f) => f.type === 'calculated');
  const groups = top.filter((f) => f.type === 'repeating_group' && (f.fields ?? []).some((x) => x.type === 'calculated'));
  if (calculated.length === 0 && groups.length === 0) return answers;

  const fields: Record<string, unknown> = { ...answers.fields };
  for (let pass = 0; pass <= calculated.length; pass++) {
    if (!computeInto(calculated, fields, () => fields)) break;
  }
  for (const g of groups) {
    const rows = fields[g.key];
    if (!Array.isArray(rows)) continue;
    const subs = (g.fields ?? []).filter((x) => x.type === 'calculated');
    fields[g.key] = rows.map((row) => {
      const r: Record<string, unknown> = { ...(row as Record<string, unknown>) };
      for (let pass = 0; pass <= subs.length; pass++) {
        if (!computeInto(subs, r, () => ({ ...fields, ...r }))) break;
      }
      return r;
    });
  }
  return { ...answers, fields };
}

/** Publish check: every formula parses and reads only known, non-circular field keys. */
export function checkFormulas(template: Template): string[] {
  const problems: string[] = [];
  const top = template.sections.flatMap((s) => s.items.filter((i): i is FieldItem => i.kind === 'field'));
  const topKeys = new Set(top.map((f) => f.key));
  const check = (f: FieldItem, keys: Set<string>, where: string) => {
    if (f.type !== 'calculated') return;
    try {
      parseFormula(f.formula ?? '');
    } catch (e) {
      problems.push(`${where}: ${(e as Error).message}`);
      return;
    }
    for (const k of formulaRefs(f.formula)) {
      if (k === f.key) problems.push(`${where}: formula refers to itself`);
      else if (!keys.has(k)) problems.push(`${where}: formula uses unknown field "${k}"`);
    }
  };
  for (const f of top) {
    check(f, topKeys, `field ${f.key}`);
    if (f.type === 'repeating_group') {
      const keys = new Set([...topKeys, ...(f.fields ?? []).map((x) => x.key)]);
      for (const sub of f.fields ?? []) check(sub, keys, `field ${f.key}.${sub.key}`);
    }
  }
  // Cycles between top-level calculated fields (a = b + 1, b = a * 2).
  const deps = new Map(top.filter((f) => f.type === 'calculated').map((f) => [f.key, formulaRefs(f.formula)]));
  const state = new Map<string, 'visiting' | 'done'>();
  const visit = (k: string): boolean => {
    if (state.get(k) === 'done' || !deps.has(k)) return false;
    if (state.get(k) === 'visiting') return true;
    state.set(k, 'visiting');
    const cyclic = deps.get(k)!.some(visit);
    state.set(k, 'done');
    return cyclic;
  };
  for (const k of deps.keys()) {
    state.clear();
    if (visit(k)) problems.push(`field ${k}: formulas form a cycle`);
  }
  return problems;
}
